/**
 * Mock VisioMynd — rPPG → HR / HRV breathing trainer.
 *
 * In real VisioMynd integration: webcam grabs face ROI, extracts pulse
 * via CHROM/POS, computes HR + RMSSD + LF/HF; in source-only mode the
 * self-camera preview is hidden from case (per Q10).
 *
 * Mock here: synthesize HR oscillation + breathing animation; emit
 * hrv.rmssd, hrv.coherence, breath.rate to BroadcastChannel.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DemoGuard, NotInProduction, isDemoHostAllowed } from "../components/DemoBanner.js";

export default function VisioDemoSubapp() {
  const [search] = useSearchParams();
  const room = search.get("room");
  const isTr = search.get("telerehab") === "1";
  const headless = search.get("headless") === "1";

  const [hr, setHr]           = useState(72);
  const [rmssd, setRmssd]     = useState(45);
  const [coherence, setCoh]   = useState(0.4);
  const [breathPhase, setBp]  = useState(0);   // 0..1 inhale/exhale cycle
  const [running, setRunning] = useState(true);
  const bcRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!isTr || !room) return;
    if (!isDemoHostAllowed()) return;
    const bc = new BroadcastChannel(`tr-${room}`);
    bcRef.current = bc;
    bc.onmessage = (ev) => {
      const m = ev.data;
      if (m.type === "start" || m.type === "resume") setRunning(true);
      if (m.type === "pause" || m.type === "stop")   setRunning(false);
    };
    bc.postMessage({ type: "subapp_state", state: "ready", source: "visio-demo" });
    return () => bc.close();
  }, [isTr, room]);

  // Breathing animation cycle (10s — resonance breathing typical 6/min)
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setBp(p => (p + 0.02) % 1);
    }, 100);
    return () => clearInterval(t);
  }, [running]);

  // Synthesize HRV metrics + emit. demo:true tags rows in metric_snapshots.
  useEffect(() => {
    if (!running) return;
    if (!isDemoHostAllowed()) return;
    const t = setInterval(() => {
      const hrNew    = 70 + Math.sin(Date.now() / 9000) * 8 + (Math.random() - 0.5) * 3;
      const rmssdNew = 40 + Math.sin(Date.now() / 12000) * 18 + (Math.random() - 0.5) * 4;
      const cohNew   = Math.max(0, Math.min(1, 0.4 + Math.sin(Date.now() / 15000) * 0.3 + (Math.random() - 0.5) * 0.05));
      setHr(hrNew);
      setRmssd(rmssdNew);
      setCoh(cohNew);

      const bc = bcRef.current;
      if (!bc) return;
      const ts = Date.now();
      bc.postMessage({ type: "metric", source: "hrv.hr",        value: hrNew,    ts, demo: true });
      bc.postMessage({ type: "metric", source: "hrv.rmssd",     value: rmssdNew, ts, demo: true });
      bc.postMessage({ type: "metric", source: "hrv.coherence", value: cohNew,   ts, demo: true });
      bc.postMessage({ type: "metric", source: "reward",        value: cohNew,   ts, demo: true });
    }, 700);
    return () => clearInterval(t);
  }, [running]);

  if (!isDemoHostAllowed()) return <NotInProduction subapp="VisioMynd" />;

  if (!isTr) {
    return <main className="page page--auth"><h1>VisioMynd Demo</h1><p className="muted">需要 ?telerehab=1&amp;room=...</p></main>;
  }

  // Headless mode = trainer picked this as source-only; case sees breathing
  // guidance only, no metric numbers (those go to trainer).
  const breathScale = 0.6 + Math.sin(breathPhase * Math.PI * 2) * 0.35;

  return (
    <DemoGuard subapp="VisioMynd · rPPG">
      <div style={{
        padding: "8px var(--s-5)", display: "flex", justifyContent: "space-between",
        background: "rgba(0,0,0,0.4)", color: "var(--paper)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        flexShrink: 0,
      }}>
        <span style={{ letterSpacing: "0.1em" }}>VISIOMYND · rPPG · TELEREHAB {headless ? "· headless" : ""}</span>
        <span>room {room?.slice(0, 12)}</span>
        <span style={{ color: running ? "var(--olive)" : "var(--warning)" }}>● {running ? "running" : "paused"}</span>
      </div>

      <div style={{ flex: 1, position: "relative", overflow: "hidden",
                    background: "radial-gradient(ellipse at center, #20534b 0%, #1c1814 80%)" }}>
        {/* Breathing circle — case follows this */}
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center", flexDirection: "column",
        }}>
          <div style={{
            width: 320, height: 320,
            borderRadius: "50%",
            border: `2px solid var(--rust)`,
            transform: `scale(${breathScale})`,
            transition: "transform 100ms linear",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `radial-gradient(circle, rgba(196, 71, 31, 0.15), transparent 70%)`,
          }}>
            <span className="italic-display" style={{
              fontSize: "var(--t-xl)", color: "var(--paper)",
              opacity: breathScale > 1 ? 0.5 : 1,
            }}>
              {breathScale > 0.85 ? "吸" : breathScale < 0.5 ? "呼" : "·"}
            </span>
          </div>
          <div style={{ marginTop: "var(--s-7)", color: "var(--paper-deep)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)" }}>
            跟著呼吸 · resonance ~ 6/min
          </div>
        </div>
      </div>

      {/* HUD — case sees only breathing guide, trainer gets numbers via BC */}
      <div style={{ padding: "var(--s-3) var(--s-5)", display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--s-5)",
                    fontFamily: "var(--mono)", fontSize: "var(--t-sm)",
                    background: "rgba(0,0,0,0.3)" }}>
        <Stat label="HR (bpm)"   value={hr.toFixed(0)} />
        <Stat label="RMSSD (ms)" value={rmssd.toFixed(0)} />
        <Stat label="Coherence" value={coherence.toFixed(2)} primary />
      </div>
    </DemoGuard>
  );
}

function Stat({ label, value, primary }: { label: string; value: string; primary?: boolean }) {
  return (
    <div>
      <div style={{ color: "var(--paper-deep)", fontSize: "var(--t-xs)" }}>{label}</div>
      <div style={{ fontSize: "var(--t-md)", color: primary ? "var(--rust)" : "var(--paper)" }}>{value}</div>
    </div>
  );
}
